import React from "react";
import { parseErrorMessage } from "@/lib/parseErrorMessage";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useAdAccount } from "@/contexts/AdAccountContext";
import { useOnboardingSettings, AdAccountSettings } from "@/hooks/useOnboardingSettings";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Save, AlertTriangle } from "lucide-react";
import { SetupCreativeEnhancements } from "@/components/setup/SetupCreativeEnhancements";
import type { SitelinkItem } from "@/components/shared/SitelinksSection";

type FormState = {
  image_enhancements: Record<string, boolean>;
  video_enhancements: Record<string, boolean>;
  carousel_enhancements: Record<string, boolean>;
  collection_enhancements: Record<string, "OPT_IN" | "OPT_OUT">;
  enhancements_catalog: Record<string, boolean>;
  flexible_advantage_plus_creative: boolean;
  sitelinks: SitelinkItem[];
};

const DEFAULT_FORM: FormState = {
  image_enhancements: {},
  video_enhancements: {},
  carousel_enhancements: {},
  collection_enhancements: {},
  enhancements_catalog: {},
  flexible_advantage_plus_creative: false,
  sitelinks: [],
};

function settingsToForm(s: AdAccountSettings): FormState {
  const rawSitelinks = (s as any)?.sitelinks;
  return {
    image_enhancements: s.image_enhancements ?? {},
    video_enhancements: s.video_enhancements ?? {},
    carousel_enhancements: (s as any).carousel_enhancements ?? {},
    collection_enhancements: (s as any).collection_enhancements ?? {},
    enhancements_catalog: s.enhancements_catalog ?? {},
    flexible_advantage_plus_creative: (s as any).flexible_advantage_plus_creative ?? false,
    sitelinks: Array.isArray(rawSitelinks) ? rawSitelinks : [],
  };
}




export default function SetupEnhancements() {
  const { selectedAdAccountId, selectedAdAccountName, adAccounts, isLoading: accountsLoading } = useAdAccount();
  const { toast } = useToast();

  const { settings, isLoading: settingsLoading, saveStep, isSaving } = useOnboardingSettings(selectedAdAccountId ?? undefined);

  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [isDirty, setIsDirty] = useState(false);
  const savedSnapshot = useRef<string>("");

  // Load settings into form
  useEffect(() => {
    if (settings) {
      const f = settingsToForm(settings);
      setForm(f);
      savedSnapshot.current = JSON.stringify(f);
      setIsDirty(false);
    } else if (!settingsLoading && selectedAdAccountId) {
      setForm(DEFAULT_FORM);
      savedSnapshot.current = JSON.stringify(DEFAULT_FORM);
      setIsDirty(false);
    }
  }, [settings, settingsLoading, selectedAdAccountId]);

  const updateForm = useCallback((partial: Partial<FormState>) => {
    setForm((prev) => {
      const next = { ...prev, ...partial };
      setIsDirty(JSON.stringify(next) !== savedSnapshot.current);
      return next;
    });
  }, []);

  // Warn on browser/tab close with unsaved changes
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const handleSave = async () => {
    if (!selectedAdAccountId) return;
    try {
      await saveStep(form as any);
      savedSnapshot.current = JSON.stringify(form);
      setIsDirty(false);
      toast({ title: "Settings saved successfully" });
    } catch (err: unknown) {
      toast({ title: "Failed to save settings", description: parseErrorMessage(err), variant: "destructive" });
    }
  };

  // No ad accounts
  if (!accountsLoading && adAccounts.length === 0) {
    return (
      <div className="flex-1 p-8">
        <div className="max-w-4xl mx-auto">
          <Card>
            <CardContent className="py-16 text-center space-y-4">
              <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto" />
              <h3 className="text-lg font-semibold">No ad accounts connected</h3>
              <p className="text-sm text-muted-foreground">
                Go to Settings to connect your Meta account.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const isLoading = accountsLoading || settingsLoading;

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-foreground">Enhancements</h1>
          {selectedAdAccountName && (
            <p className="text-sm text-muted-foreground mt-1">
              {selectedAdAccountName}
            </p>
          )}
        </div>

        {isLoading ? (
          <div className="space-y-6">
            <Skeleton className="h-96 w-full rounded-lg" />
          </div>
        ) : selectedAdAccountId ? (
          <>
            <SetupCreativeEnhancements
              data={{
                image_enhancements: form.image_enhancements,
                video_enhancements: form.video_enhancements,
                carousel_enhancements: form.carousel_enhancements,
                collection_enhancements: form.collection_enhancements,
                enhancements_catalog: form.enhancements_catalog,
                flexible_advantage_plus_creative: form.flexible_advantage_plus_creative,
                sitelinks: form.sitelinks,
              }}
              onChange={updateForm}
            />

            {/* Save Button */}
            <Button
              onClick={handleSave}
              disabled={!isDirty || isSaving}
              className="w-full"
              size="lg"
            >
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? "Saving..." : isDirty ? "Save Changes" : "Settings Saved"}
            </Button>
          </>
        ) : null}
      </div>
    </div>
  );
}
